import React, { useState, useEffect } from 'react';
import MetadataDropdown from 'components/metadata/metadata-dropdown';
import { apiService } from 'services/base.service';
import { MetadataType } from './types';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import useDashboardContext from 'contexts/dashboard.context';
import { CreateMetadataResponse, MetadataItemType } from 'pages/metadata/types';
import useNewShopContext from 'contexts/create-shop.context';

const CREATE_ENDPOINT = {
  brand: 'keywords/brand_keyword',
  descriptive: 'keywords/brand_descriptive_metadata',
  business: 'keywords/brand_business_metadata'
};

const CreateShopMetadata = () => {
  const { category } = useDashboardContext();
  const { createShopForm, setCreateShopForm } = useNewShopContext();
  const [keywordsList, setKeywordsList] = useState<MetadataType[]>([]);
  const [descriptiveMetadataList, setDescriptiveMetadataList] = useState<MetadataType[]>([]);
  const [businessMetadataList, setBusinessMetadataList] = useState<MetadataType[]>([]);

  const [selectedKeywords, setSelectedKeywords] = useState<MetadataItemType[]>([
    ...createShopForm.brand_keywords
  ]);
  const [selectedDescriptiveMetadata, setSelectedDescriptiveMetadata] = useState<
    MetadataItemType[]
  >([...createShopForm.brand_descriptive_metadatas]);
  const [selectedBusinessMetadata, setSelectedBusinessMetadata] = useState<MetadataItemType[]>([
    ...createShopForm.brand_business_metadatas
  ]);

  const MAP_LOCAL_STATE = {
    brand: setKeywordsList,
    descriptive: setDescriptiveMetadataList,
    business: setBusinessMetadataList
  };

  const getKeywordList = async () => {
    try {
      const [resBrand, resBusiness, resDes] = await Promise.all([
        apiService.get(`${CREATE_ENDPOINT.brand}`),
        apiService.get(`${CREATE_ENDPOINT.business}`),
        apiService.get(`${CREATE_ENDPOINT.descriptive}`)
      ]);

      const brandMetadata: MetadataType[] = resBrand.data.data;
      const businessMetadata: MetadataType[] = resBusiness.data.data;
      const descriptiveMetadata: MetadataType[] = resDes.data.data;

      setKeywordsList(brandMetadata);
      setDescriptiveMetadataList(descriptiveMetadata);
      setBusinessMetadataList(businessMetadata);
    } catch (error) {
      toast(<ToastMessage type='error' message={'Failed to fetch'} />);
    }
  };

  useEffect(() => {
    getKeywordList();
  }, []);
  useEffect(() => {
    setCreateShopForm({
      ...createShopForm,
      brand_descriptive_metadatas: selectedDescriptiveMetadata.map((metadata) => {
        return {
          name: metadata.name,
          id: metadata.id
        };
      }),
      brand_business_metadatas: selectedBusinessMetadata.map((metadata) => {
        return {
          name: metadata.name,
          id: metadata.id
        };
      }),
      brand_keywords: selectedKeywords.map((keyword) => {
        return {
          name: keyword.name,
          id: keyword.id
        };
      })
    });
  }, [selectedKeywords, selectedDescriptiveMetadata, selectedBusinessMetadata]);

  const onKeywordTagClick = (item: MetadataItemType) => {
    const exists = !!selectedKeywords.find((k) => k.id === item.id);
    if (!exists) {
      setSelectedKeywords([...selectedKeywords, item]);
    } else {
      setSelectedKeywords((words) => words.filter((w) => w.id !== item.id));
    }
  };
  const onDescriptiveTagClick = (item: MetadataItemType) => {
    const exists = !!selectedDescriptiveMetadata.find((k) => k.id === item.id);
    if (!exists) {
      setSelectedDescriptiveMetadata([...selectedDescriptiveMetadata, item]);
    } else {
      setSelectedDescriptiveMetadata((words) => words.filter((w) => w.id !== item.id));
    }
  };
  const onBusinessTagClick = (item: MetadataItemType) => {
    const exists = !!selectedBusinessMetadata.find((k) => k.id === item.id);
    if (!exists) {
      setSelectedBusinessMetadata([...selectedBusinessMetadata, item]);
    } else {
      setSelectedBusinessMetadata((words) => words.filter((w) => w.id !== item.id));
    }
  };

  const createMetadata = async (name: string, type: keyof typeof CREATE_ENDPOINT) => {
    const API_URL = CREATE_ENDPOINT[type];
    try {
      const res = await apiService.post(API_URL, {
        data: {
          name,
          item_type: category?.toUpperCase()
        }
      });
      const { message, id } = res.data as CreateMetadataResponse;
      toast(<ToastMessage type='success' message={message} />);
      MAP_LOCAL_STATE[type]((p) => [{ name, id }, ...p]);
    } catch {
      toast(<ToastMessage type='error' message='There was a problem adding metadata' />);
    }
  };

  return (
    <div>
      <MetadataDropdown
        title='Keywords'
        description='Select keyword'
        metadataList={keywordsList}
        selectedMetadata={createShopForm.brand_keywords}
        onCreate={(w) => createMetadata(w, 'brand')}
        onTagClick={onKeywordTagClick}
      />
      <MetadataDropdown
        title='Descriptive Metadata'
        description='Select descriptive metadata'
        metadataList={descriptiveMetadataList}
        selectedMetadata={createShopForm.brand_descriptive_metadatas}
        onCreate={(w) => createMetadata(w, 'descriptive')}
        onTagClick={onDescriptiveTagClick}
      />
      <MetadataDropdown
        title='Business Metadata'
        description='Select Business metadata'
        metadataList={businessMetadataList}
        selectedMetadata={createShopForm.brand_business_metadatas}
        onCreate={(w) => createMetadata(w, 'business')}
        onTagClick={onBusinessTagClick}
      />
    </div>
  );
};

export default CreateShopMetadata;
